"use client"
import Tooltip from "@/ui/Tooltip"
import {
  CHART_MAX_WIDTH,
} from "../../../power-bubble-chart/powerBubbleChartLayout"
import {
  DistributionOfPowerTooltipContent,
} from "../../../power-bubble-chart/distribution-of-power-tooltip/DistributionOfPowerTooltipContent"
import { useHudContext } from "../../shared/playbookHUDInfra"
import { MetricTrigger } from "../../metric-details/MetricTrigger"
import {
  PowerTreemap,
  type PowerCellLayoutMode,
} from "./distribution-of-power-bubbles/PowerTreemap"
import { usePowerRegions } from "./distribution-of-power-bubbles/usePowerRegions"
import {
  type GetCellLogoTreatment,
  type GetTreemapCellStyle,
} from "./distribution-of-power-bubbles/powerTreemapStyle"
import { type TreemapCornerLayout } from "./distribution-of-power-bubbles/powerTreemapStyleCorner"
import { useDistributionOfPowerTooltip } from "./distribution-of-power-bubbles/useDistributionOfPowerTooltip"
import { useMeasuredPowerBlock } from "./distribution-of-power-bubbles/useMeasuredPowerBlock"
import { usePowerHoverSwap } from "./powerHoverSwap"
import {
  HudHoverPanel,
  PowerBreakdownPanelContent,
  useUnifiedHudPanels,
} from "../panels/unifiedHoverPanels"

type DistributionOfPowerBubbles4Props = {
  align?: "start" | "end"
  className?: string
  height?: number
  maxWidth?: number
  showTransparencyLevels?: boolean
  // Override the per-cell style / logo treatment to swap the lab-transparency
  // visualization (e.g. the fade variant) while reusing the rest of the treemap.
  getCellStyle?: GetTreemapCellStyle
  getLogoTreatment?: GetCellLogoTreatment
  layoutMode?: PowerCellLayoutMode
  groupByCountry?: boolean
  countryGroupGapPx?: number
  gapPx?: number
  borderRadiusPx?: number
  // When set, each cell's lab icon is sized to this fraction of the cell's
  // smaller dimension (e.g. 0.3) instead of the default clamped logo size.
  logoSizeFraction?: number
  // When false, labs that aren't yet "frontier AI" (borderOnly) are hidden
  // from the chart entirely. Defaults to true (the existing behavior — they
  // are drawn as border-only cells).
  includeNonFrontier?: boolean
  // When set, cells render the corner layout (icon on a black square top-left,
  // share number top-right). See DistributionOfPowerBubbles4Corner.
  cornerLayout?: TreemapCornerLayout
}

const STRIP_BLOCK_HEIGHT = 40

// Whole-world treemap version: every lab competes for area inside one block.
export const DistributionOfPowerBubbles4 = ({
  align = "start",
  className,
  height = STRIP_BLOCK_HEIGHT,
  maxWidth = CHART_MAX_WIDTH,
  showTransparencyLevels,
  getCellStyle,
  getLogoTreatment,
  layoutMode,
  groupByCountry,
  countryGroupGapPx,
  gapPx,
  borderRadiusPx,
  logoSizeFraction,
  includeNonFrontier,
  cornerLayout,
}: DistributionOfPowerBubbles4Props) => {
  const ctx = useHudContext()
  const hudMetrics = ctx.currentPhase?.hudMetrics
  const labTransparencyLevel = showTransparencyLevels === false
    ? undefined
    : hudMetrics?.transparencyFrontierProjects
  const hiddenStyle = ctx.epilogue.isEpilogueHudMode
    ? ctx.epilogue.epilogueHiddenStyle
    : ctx.galaxyHideStyle

  const regions = usePowerRegions()

  const localTooltip = useDistributionOfPowerTooltip()
  // Inside a <PowerHoverSwap> provider the floating tooltip is suppressed:
  // hover is reported to the swap context instead (which also feeds the
  // treemap's cell highlighting), and a PowerHoverSwapTarget elsewhere on the
  // HUD renders the distribution-of-power info in-canvas.
  const hoverSwap = usePowerHoverSwap()
  // Inside a <UnifiedHudPanels> (no swap provider) the floating tooltip
  // renders the unified DISTRIBUTION OF POWER card instead.
  const unifiedPanels = useUnifiedHudPanels()
  const hoveredItem = hoverSwap ? hoverSwap.hoveredItem : localTooltip.hoveredItem
  const setHoveredItem = hoverSwap ? hoverSwap.setHoveredItem : localTooltip.setHoveredItem
  // In swap mode the takeover does NOT clear when the pointer exits the
  // treemap — the enclosing HoverSwapStickyZone holds it across the gap
  // between the columns, and entering the left column dismisses it (see
  // hoverSwapStickyZone.tsx). The floating tooltip still clears on leave.
  const clearHoveredItem = hoverSwap ? undefined : localTooltip.clearHoveredItem
  const { blockRef, blockSize } = useMeasuredPowerBlock({
    width: maxWidth,
    height,
  })

  const body = (
    <div
      className="text-[#303030]"
      style={{
        width: "100%",
        maxWidth,
        marginLeft: align === "end" ? "auto" : undefined,
      }}
    >
      <div
        ref={blockRef}
        className="relative w-full overflow-hidden"
        aria-label="Compute distribution"
        style={{
          height,
        }}
      >
        <PowerTreemap
          regions={regions}
          blockSize={blockSize}
          hoveredItem={hoveredItem}
          labTransparencyLevel={labTransparencyLevel}
          onHoverItem={setHoveredItem}
          getCellStyle={getCellStyle}
          getLogoTreatment={getLogoTreatment}
          layoutMode={layoutMode}
          groupByCountry={groupByCountry}
          countryGroupGapPx={countryGroupGapPx}
          gapPx={gapPx}
          borderRadiusPx={borderRadiusPx}
          logoSizeFraction={logoSizeFraction}
          includeNonFrontier={includeNonFrontier}
          cornerLayout={cornerLayout}
        />
      </div>
    </div>
  )

  return (
    <MetricTrigger
      id="power"
      className={className}
      onMouseLeave={clearHoveredItem}
      style={hiddenStyle}
    >
      {hoverSwap ? body : (
        <Tooltip
          placement="left-start"
          maxWidth={360}
          disableShadow={Boolean(unifiedPanels)}
          content={
            unifiedPanels ? (
              // Clicking the treemap opens the power details panel
              // (MetricTrigger id="power"), so the card advertises it.
              <HudHoverPanel title="Distribution of Power" widthPx={280} showDetailsFooter>
                <PowerBreakdownPanelContent
                  regions={regions}
                  hoveredItem={hoveredItem}
                />
              </HudHoverPanel>
            ) : (
              <DistributionOfPowerTooltipContent
                regions={regions}
                hoveredItem={hoveredItem}
              />
            )
          }
        >
          {body}
        </Tooltip>
      )}
    </MetricTrigger>
  )
}
